import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
    FaBriefcase, FaCalendarAlt, FaCheckCircle, FaTimesCircle,
    FaClock, FaRegHandshake, FaEye, FaMapMarkerAlt,
    FaBuilding, FaTimes, FaCalendarCheck, FaHourglassHalf, FaBan,
    FaSearch, FaVideo
} from 'react-icons/fa';
import { showError } from '../../../utils/Alert';
import { API_URL } from '../../../config';
import Loader from '../../shared/Loader';
import '../../Student/views/StudentApplications.css';

/* Colour palette for company logos */
const LOGO_COLORS = [
    ['#1E3A8A', '#3B82F6'], ['#065F46', '#10B981'], ['#7C2D12', '#F97316'],
    ['#4C1D95', '#8B5CF6'], ['#831843', '#EC4899'], ['#1E3A8A', '#6366F1'],
    ['#134E4A', '#14B8A6'], ['#713F12', '#EAB308'], ['#0C4A6E', '#0EA5E9'], ['#3F1F1F', '#EF4444']
];
const getLogoColor = (name = '') => LOGO_COLORS[(name.charCodeAt(0) || 65) % LOGO_COLORS.length];

const STATUS_CONFIG = {
    Pending: { label: 'Under Review', color: '#92400E', bg: '#FEF3C7', icon: <FaHourglassHalf /> },
    Shortlisted: { label: 'Shortlisted ✓', color: '#065F46', bg: '#D1FAE5', icon: <FaCheckCircle /> },
    Rejected: { label: 'Not Selected', color: '#B91C1C', bg: '#FEE2E2', icon: <FaTimesCircle /> },
    'Interview Scheduled': { label: 'Interview Scheduled', color: '#1D4ED8', bg: '#DBEAFE', icon: <FaCalendarCheck /> },
    'Interview Completed': { label: 'Interview Completed', color: '#065F46', bg: '#D1FAE5', icon: <FaCheckCircle /> },
    Withdrawn: { label: 'Withdrawn', color: '#475569', bg: '#F1F5F9', icon: <FaBan /> }
};

const ApplicationsTracker = () => {
    const [applications, setApplications] = useState([]);
    const [interviews, setInterviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);   // For detail modal
    const [searchTerm, setSearchTerm] = useState('');
    const [locationFilter, setLocationFilter] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                const [appsRes, interviewsRes] = await Promise.all([
                    axios.get('/api/student/applications', { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get('/api/student/interviews', { headers: { Authorization: `Bearer ${token}` } })
                ]);
                setApplications(appsRes.data);
                setInterviews(interviewsRes.data);
            } catch {
                showError('Error', 'Failed to fetch your applications');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredApplications = applications.filter(app => {
        const job = app.jobId || {};
        const matchesSearch = (job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.company?.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesLocation = job.location?.toLowerCase().includes(locationFilter.toLowerCase());
        return matchesSearch && matchesLocation;
    });

    if (loading) return <Loader />;

    return (
        <div className="applications-tracker fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Application Tracker</h1>
                <p className="page-subtitle">Track your journey from application to placement.</p>
            </div>

            {/* Search Strip (Mirrors Job Portal) */}
            <div className="job-search-strip">
                <div style={{ flex: 1, position: 'relative' }}>
                    <FaSearch className="sjp-search-icon" />
                    <input
                        className="glass-input sjp-search-input"
                        placeholder="Search by role or company…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div style={{ flex: 0.5, position: 'relative' }}>
                    <FaMapMarkerAlt className="sjp-search-icon" />
                    <input
                        className="glass-input sjp-search-input"
                        placeholder="Location…"
                        value={locationFilter}
                        onChange={e => setLocationFilter(e.target.value)}
                    />
                </div>
            </div>

            <div className="application-list">
                {filteredApplications.length > 0 ? filteredApplications.map(app => {
                    const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.Pending;
                    const [from, to] = getLogoColor(app.jobId?.company);
                    const job = app.jobId;

                    return (
                        <div key={app._id} className="app-card" onClick={() => setSelected(app)} style={{ cursor: 'pointer' }}>
                            {/* Left: Status stripe */}
                            <div className="app-status-stripe" style={{ background: cfg.color }} />

                            <div
                                className="app-company-logo"
                                style={{ background: job?.companyId?.logoUrl ? 'white' : `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}
                            >
                                {job?.companyId?.logoUrl ? (
                                    <img
                                        src={`${API_URL}${job.companyId.logoUrl}`}
                                        alt={job?.company}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                                    />
                                ) : (
                                    <span>{job?.company?.charAt(0)?.toUpperCase() || '?'}</span>
                                )}
                            </div>

                            {/* Info */}
                            <div className="app-info-area">
                                <h4 className="app-job-title">{job?.title || 'Unknown Position'}</h4>
                                <p className="app-company-name">{job?.company || 'Unknown Company'}</p>
                                <div className="app-meta-row">
                                    {job?.location && (
                                        <span className="app-meta-chip app-chip-loc">
                                            <FaMapMarkerAlt /> {job.location}
                                        </span>
                                    )}
                                    <span className="app-meta-chip app-chip-date">
                                        <FaCalendarAlt /> Applied {new Date(app.appliedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </span>
                                    {(job?.salaryRange || job?.salary) && (
                                        <span className="app-meta-chip app-chip-salary">
                                            Pay: {job.salaryRange || job.salary}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Right: Status + View */}
                            <div className="app-right-area">
                                <span className="app-status-pill" style={{ background: cfg.bg, color: cfg.color }}>
                                    {cfg.icon} {cfg.label}
                                </span>
                                <button className="app-view-btn">
                                    <FaEye /> View Details
                                </button>
                            </div>
                        </div>
                    );
                }) : (
                    <div className="app-empty-state">
                        <FaBriefcase size={52} />
                        <h3>{applications.length > 0 ? 'No matching applications' : 'No applications yet'}</h3>
                        <p>
                            {applications.length > 0
                                ? 'Try adjusting your search or location filters.'
                                : 'Apply for jobs in the Career Gateway to see them tracked here.'}
                        </p>
                    </div>
                )}
            </div>

            {/* ---- Application Detail Modal (portaled) ---- */}
            {selected && ReactDOM.createPortal(
                <div className="sa-modal-overlay" onClick={() => setSelected(null)}>
                    <div className="sa-modal-box" onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className="sa-modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div
                                    className="app-company-logo"
                                    style={{
                                        width: '52px', height: '52px', fontSize: '22px', borderRadius: '14px',
                                        background: selected.jobId?.companyId?.logoUrl ? 'white' : `linear-gradient(135deg, ${getLogoColor(selected.jobId?.company)[0]} 0%, ${getLogoColor(selected.jobId?.company)[1]} 100%)`
                                    }}
                                >
                                    {selected.jobId?.companyId?.logoUrl ? (
                                        <img
                                            src={`${API_URL}${selected.jobId.companyId.logoUrl}`}
                                            alt={selected.jobId?.company}
                                            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '10px' }}
                                        />
                                    ) : (
                                        <span>{selected.jobId?.company?.charAt(0)?.toUpperCase() || '?'}</span>
                                    )}
                                </div>
                                <div>
                                    <h2 className="sa-modal-title">{selected.jobId?.title || 'Unknown Position'}</h2>
                                    <p className="sa-modal-company">{selected.jobId?.company || 'Unknown Company'}</p>
                                </div>
                            </div>
                            <button className="sa-close-btn" onClick={() => setSelected(null)}><FaTimes /></button>
                        </div>

                        {/* Body */}
                        <div className="sa-modal-body">
                            {/* Status banner */}
                            {(() => {
                                const cfg = STATUS_CONFIG[selected.status] || STATUS_CONFIG.Pending;
                                return (
                                    <div className="sa-status-banner" style={{ background: cfg.bg, borderColor: cfg.color }}>
                                        <span className="sa-status-icon" style={{ color: cfg.color }}>{cfg.icon}</span>
                                        <div>
                                            <p className="sa-status-label" style={{ color: cfg.color }}>{cfg.label}</p>
                                            <p className="sa-status-desc">Current application status</p>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Info grid */}
                            <div className="sa-info-grid">
                                <div className="sa-info-item">
                                    <span>Applied On</span>
                                    <p>{new Date(selected.appliedAt).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' })}</p>
                                </div>
                                {selected.jobId?.location && (
                                    <div className="sa-info-item">
                                        <span>Location</span>
                                        <p><FaMapMarkerAlt style={{ marginRight: 4, color: '#1D4ED8' }} />{selected.jobId.location}</p>
                                    </div>
                                )}
                                {(selected.jobId?.salaryRange || selected.jobId?.salary) && (
                                    <div className="sa-info-item">
                                        <span>Package Offered</span>
                                        <p className="sa-salary-hl">Pay: {selected.jobId.salaryRange || selected.jobId.salary}</p>
                                    </div>
                                )}
                                {selected.jobId?.experience && (
                                    <div className="sa-info-item">
                                        <span>Experience Required</span>
                                        <p>{selected.jobId.experience}</p>
                                    </div>
                                )}
                                {selected.jobId?.type && (
                                    <div className="sa-info-item">
                                        <span>Job Type</span>
                                        <p>{selected.jobId.type}</p>
                                    </div>
                                )}
                            </div>

                            {/* Interview Details Panel */}
                            {(selected.status === 'Interview Scheduled' || selected.status === 'Interview Completed') && (() => {
                                const iv = interviews.find(i => i.applicationId === selected._id || i.applicationId?._id === selected._id);
                                if (!iv) return null;
                                return (
                                    <div className="sa-section sa-interview-panel">
                                        <h4><FaCalendarCheck style={{ color: '#1D4ED8', marginRight: 6 }} />Interview Details</h4>
                                        <div className="sa-info-grid" style={{ marginBottom: 0 }}>
                                            <div className="sa-info-item">
                                                <span>Date</span>
                                                <p>{new Date(iv.scheduledDate).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' })}</p>
                                            </div>
                                            <div className="sa-info-item">
                                                <span>Time Slot</span>
                                                <p><FaClock style={{ marginRight: 4, color: '#1D4ED8' }} />{iv.timeSlot}</p>
                                            </div>
                                            {iv.notes && (
                                                <div className="sa-info-item" style={{ gridColumn: '1/-1' }}>
                                                    <span>Preparation Notes</span>
                                                    <p>{iv.notes}</p>
                                                </div>
                                            )}
                                        </div>
                                        {iv.meetLink && selected.status === 'Interview Scheduled' && (
                                            <a href={iv.meetLink} target="_blank" rel="noreferrer" className="sa-join-btn" style={{ marginTop: '16px', display: 'inline-flex', textDecoration: 'none' }}>
                                                <FaVideo /> Join Interview
                                            </a>
                                        )}
                                        {selected.status === 'Interview Completed' && (
                                            <div style={{ marginTop: '12px', padding: '10px 16px', background: '#D1FAE5', borderRadius: '10px', color: '#065F46', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <FaCheckCircle /> Interview completed. Awaiting recruiter's final decision.
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Feedback */}
                            {selected.feedback && (
                                <div className="sa-section">
                                    <h4>Recruiter Feedback</h4>
                                    <p>{selected.feedback}</p>
                                </div>
                            )}

                            {/* Description */}
                            {selected.jobId?.description && (
                                <div className="sa-section">
                                    <h4>Job Description</h4>
                                    <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.75' }}>{selected.jobId.description}</p>
                                </div>
                            )}

                            {/* Cover Letter */}
                            {selected.coverLetter && (
                                <div className="sa-section">
                                    <h4>Your Cover Letter</h4>
                                    <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.75' }}>{selected.coverLetter}</p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="sa-modal-footer">
                            <button className="sa-close-action" onClick={() => setSelected(null)}>Close</button>
                            {selected.status === 'Interview Scheduled' && (() => {
                                const iv = interviews.find(i => i.applicationId === selected._id || i.applicationId?._id === selected._id);
                                return iv?.meetLink ? (
                                    <a
                                        href={iv.meetLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="sa-join-btn"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <FaVideo /> Join Interview
                                    </a>
                                ) : (
                                    <button className="sa-join-btn" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                                        <FaVideo /> Link Not Available
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ApplicationsTracker;
